import uniqueId from 'lodash/uniqueId'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { getResourceGlyph, Size } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import {
  Button,
  ButtonSize,
  Checkbox,
  Icon,
  QuasiTabs,
  SingleSelect,
  Switch,
} from '@opallabs/lapidary'
import {
  type CalendarDensity,
  type CalendarNamePriority,
  type WeeksMode,
} from '../../MonthCalendarGrid/types'
import { type CalendarItemKind } from '../../types'
import { type MonthViewSettings } from '../useMonthViewSettings'
import * as styles from './styles'

/** The object families the menu asks about, and can open a pane for. */
export type ViewMenuObject = Extract<CalendarItemKind, 'content' | 'moment'>

const KINDS: ReadonlyArray<{
  label: string
  /**
   * What the pane the row opens is called. A kind with nothing to set has no
   * pane, and no way into one.
   */
  paneTitle?: string
  value: ViewMenuObject
}> = [
  { label: 'Moments', value: 'moment' },
  { label: 'Content', paneTitle: 'Content settings', value: 'content' },
]

/** One checkbox in the Other Details section. */
type ObjectDetail = Readonly<{
  isOn: (settings: MonthViewSettings) => boolean
  label: string
  set: (isOn: boolean) => Partial<MonthViewSettings>
}>

const CONTENT_DETAILS: readonly ObjectDetail[] = [
  {
    isOn: (settings) => settings.shouldShowTime,
    label: 'Start time',
    set: (shouldShowTime) => ({ shouldShowTime }),
  },
  {
    isOn: (settings) => settings.shouldShowWorkflow,
    label: 'Workflow status',
    set: (shouldShowWorkflow) => ({ shouldShowWorkflow }),
  },
]

const DENSITIES: ReadonlyArray<{ label: string; value: CalendarDensity }> = [
  { label: 'Row', value: 'row' },
  { label: 'Card', value: 'card' },
]

const WEEKS_MODES: ReadonlyArray<{ label: string; value: WeeksMode }> = [
  { label: 'Fixed', value: 'fixed' },
  { label: 'Scroll', value: 'scroll' },
]

/** A content item's two names, as the menu calls them. */
const NAME_LABELS: Readonly<Record<CalendarNamePriority, string>> = {
  content: 'Content name',
  moment: 'Moment name',
}

const OTHER_NAME: Readonly<Record<CalendarNamePriority, CalendarNamePriority>> =
  { content: 'moment', moment: 'content' }

/** The secondary title's answer when the primary is the only name drawn. */
const NO_SECONDARY = 'none'

const isNameShown = (
  settings: MonthViewSettings,
  name: CalendarNamePriority
) =>
  name === 'content'
    ? settings.shouldShowContentName
    : settings.shouldShowMomentBadge

const showName = (
  name: CalendarNamePriority,
  isOn: boolean
): Partial<MonthViewSettings> =>
  name === 'content'
    ? { shouldShowContentName: isOn }
    : { shouldShowMomentBadge: isOn }

/** What the month is coloured by when no label set has been chosen. */
const PRIMARY_LABEL = 'Primary label'

/** One of the workspace's label sets, as the menu offers it. */
export type ViewMenuLabelSet = Readonly<{ id: string; label: string }>

export type ViewMenuProps = Readonly<{
  /** The pane to open on, for a story or a test that starts inside one. */
  initialPane?: ViewMenuObject
  /**
   * The workspace's label sets, one of which the month can take its colours
   * from. Empty offers the primary label alone.
   */
  labelSets?: readonly ViewMenuLabelSet[]
  onChange: (change: Partial<MonthViewSettings>) => void
  settings: MonthViewSettings
}>

/**
 * Everything about how the month is drawn, in one sheet.
 *
 * The sheet asks about objects first: a row per kind says whether the calendar
 * draws it, and opens that kind's own settings in place rather than in a menu
 * of its own — the sheet is narrow, and a list that flies out of it is hard to
 * keep a cursor inside of. Nothing here closes the sheet, so a person can set
 * several things at once and watch the calendar answer.
 */
export const ViewMenu: React.FC<ViewMenuProps> = ({
  initialPane = null,
  labelSets = [],
  onChange,
  settings,
}) => {
  const [pane, setPane] = useState<ViewMenuObject | null>(initialPane)
  const theme = useTheme()
  const idPrefix = useMemo(() => uniqueId('month-view-detail'), [])

  const backRef = useRef<HTMLButtonElement>(null)
  const paneButtons = useRef(
    new Map<ViewMenuObject, HTMLButtonElement | null>()
  )
  const shownPane = useRef<ViewMenuObject | null>(initialPane)

  // A pane replaces the sheet's rows rather than opening beside them, so the
  // row the keyboard was on stops existing. Hand focus to the way back out, and
  // to the button that opened the pane on the way back, or the sheet loses its
  // place.
  useEffect(() => {
    if (pane === shownPane.current) return
    if (pane !== null) backRef.current?.focus()
    else if (shownPane.current !== null)
      paneButtons.current.get(shownPane.current)?.focus()
    shownPane.current = pane
  }, [pane])

  const isShown = (kind: ViewMenuObject) => settings.shownKinds.includes(kind)

  const toggleKind = (kind: ViewMenuObject) => {
    const shownKinds = isShown(kind)
      ? settings.shownKinds.filter((shown) => shown !== kind)
      : KINDS.map((each) => each.value).filter(
          (each) => each === kind || isShown(each)
        )
    // A calendar showing nothing has no way back to itself.
    if (shownKinds.length === 0) return
    onChange({ shownKinds })
  }

  const title = (label: string) => <p css={styles.viewMenuTitle}>{label}</p>

  /** The way out of a pane, carrying the pane's name: inside a pane it is the sheet's title. */
  const backRow = (label: string) => (
    <button
      ref={backRef}
      css={styles.viewMenuBackRow}
      type="button"
      onClick={() => setPane(null)}
    >
      <span css={styles.viewMenuBackIcon}>
        <Icon ariaMode="hidden" glyph="gem-chevron-w" size={Size.Icon} />
      </span>
      <span css={styles.viewMenuTitle}>{label}</span>
    </button>
  )

  const objectRow = ({ label, paneTitle, value }: (typeof KINDS)[number]) => {
    const isOn = isShown(value)

    return (
      <div key={value} css={styles.viewMenuObjectRow}>
        <span css={styles.viewMenuSwitch}>
          <Switch
            checked={isOn}
            // The switch carries the kind's name for assistive technology; the
            // text beside it is what everybody else reads.
            checkedLabel={label}
            // A calendar showing nothing has no way back to itself, so the last
            // kind standing refuses rather than quietly ignoring the click.
            readonly={isOn && settings.shownKinds.length === 1}
            uncheckedLabel={label}
            onToggle={() => toggleKind(value)}
          />
        </span>
        <Icon
          ariaMode="hidden"
          glyph={getResourceGlyph(theme, value, 'outlined')}
          size={Size.Icon}
        />
        <span css={styles.viewMenuRowLabel}>{label}</span>
        {paneTitle !== undefined && (
          <Button
            ref={(button) => {
              paneButtons.current.set(value, button)
            }}
            ariaLabel={paneTitle}
            disabled={!isOn}
            icon={<Icon ariaMode="hidden" glyph="gem-chevron-e" />}
            name={`${value}-settings`}
            size={ButtonSize.Small}
            variant="ghost"
            onClick={() => setPane(value)}
          />
        )}
      </div>
    )
  }

  const switchRow = (
    label: string,
    isOn: boolean,
    onToggle: (next: boolean) => void
  ) => (
    <div css={styles.viewMenuRow}>
      <span css={styles.viewMenuRowLabel}>{label}</span>
      <span css={styles.viewMenuSwitch}>
        <Switch
          checked={isOn}
          checkedLabel={label}
          uncheckedLabel={label}
          onToggle={onToggle}
        />
      </span>
    </div>
  )

  const segmentRow = <T extends string>(
    label: string,
    options: ReadonlyArray<{ label: string; value: T }>,
    value: T,
    onSelect: (next: T) => void,
    { isTitle = false }: { isTitle?: boolean } = {}
  ) => (
    <div css={styles.viewMenuRow}>
      <span css={isTitle ? styles.viewMenuTitle : styles.viewMenuRowLabel}>
        {label}
      </span>
      <QuasiTabs
        aria-label={label}
        value={value}
        onValueChange={(next) => onSelect(next as T)}
      >
        {options.map((option) => (
          <QuasiTabs.Option key={option.value} value={option.value}>
            {option.label}
          </QuasiTabs.Option>
        ))}
      </QuasiTabs>
    </div>
  )

  /**
   * A row whose answer is one of a list: a select, drawn as the design's
   * "select button" — the answer and a chevron, with no box round them until
   * the list is open. A select rather than tabs, because the list can be long
   * (a workspace can hold many label sets), and because "none" is one of the
   * answers rather than the absence of one.
   */
  const selectRow = <T extends string>(
    label: string,
    options: ReadonlyArray<{ label: string; value: T }>,
    value: T,
    onSelect: (next: T) => void
  ) => {
    const menuOptions: Array<MenuOption<T>> = options.map((option) => ({
      label: option.label,
      // The chosen answer wears a mark; the others wear nothing.
      trailingNode: <Icon ariaHidden={true} glyph="gem-check-circle" />,
      value: option.value,
    }))
    const chosen = menuOptions.find((option) => option.value === value) ?? null

    return (
      <div css={styles.viewMenuRow}>
        <span css={styles.viewMenuRowLabel}>{label}</span>
        <span css={styles.viewMenuSelect}>
          <SingleSelect.ControlledRoot
            options={menuOptions}
            selectedOption={chosen}
            onChange={(option) => option && onSelect(option.value)}
          >
            <SingleSelect.Body>
              <SingleSelect.Trigger
                ariaLabel={label}
                hideTrailingNode={true}
                inputWidth="auto"
              >
                <SingleSelect.TriggerText />
              </SingleSelect.Trigger>
              {/* The select sits at the end of its row, so its menu hangs
                  inward from that edge rather than off the sheet. */}
              <SingleSelect.Menu align="end" portalled={true}>
                <SingleSelect.MenuItems hideTrailingNodeUnlessSelected={true} />
              </SingleSelect.Menu>
            </SingleSelect.Body>
          </SingleSelect.ControlledRoot>
        </span>
      </div>
    )
  }

  const detailRows = (details: readonly ObjectDetail[]) =>
    details.map((detail, index) => {
      const id = `${idPrefix}-${index}`

      return (
        <div key={detail.label} css={styles.viewMenuCheckRow}>
          <Checkbox
            checked={detail.isOn(settings)}
            htmlId={id}
            onChange={(event) => onChange(detail.set(event.target.checked))}
          />
          <label css={styles.viewMenuCheckLabel} htmlFor={id}>
            {detail.label}
          </label>
        </div>
      )
    })

  const section = (
    heading: React.ReactNode,
    rows: React.ReactNode,
    rowGap: number,
    headingGap = styles.SECTION_HEADING_GAP
  ) => (
    <section css={styles.viewMenuSection(headingGap)}>
      {heading}
      <div css={styles.viewMenuRows(rowGap)}>{rows}</div>
    </section>
  )

  if (pane === 'content') {
    // The primary name is always drawn. The secondary is the other name, or
    // nothing: the setting that shows the other name is what it sets.
    const primary = settings.namePriority
    const secondary = OTHER_NAME[primary]
    const isSecondaryShown = isNameShown(settings, secondary)

    return (
      <>
        {backRow('Content settings')}

        <div css={styles.viewMenuBlock}>
          {segmentRow(
            'Density',
            DENSITIES,
            settings.contentDensity,
            (contentDensity) => onChange({ contentDensity })
          )}
          {switchRow(
            'Show duration',
            settings.shouldShowContentDuration,
            (shouldShowContentDuration) =>
              onChange({ shouldShowContentDuration })
          )}
        </div>

        <hr css={styles.viewMenuDivider} />

        {section(
          title('Titles'),
          <>
            {selectRow(
              'Primary',
              (['content', 'moment'] as const).map((name) => ({
                label: NAME_LABELS[name],
                value: name,
              })),
              primary,
              (next) =>
                onChange({
                  namePriority: next,
                  ...showName(next, true),
                  // Whether a second name is drawn is the person's choice, not
                  // the primary's: it comes along with the swap.
                  ...showName(OTHER_NAME[next], isSecondaryShown),
                })
            )}
            {selectRow(
              'Secondary',
              [
                { label: 'None', value: NO_SECONDARY },
                { label: NAME_LABELS[secondary], value: secondary },
              ],
              isSecondaryShown ? secondary : NO_SECONDARY,
              (next) => onChange(showName(secondary, next !== NO_SECONDARY))
            )}
          </>,
          styles.TITLE_ROW_GAP
        )}

        <hr css={styles.viewMenuDivider} />

        {section(
          title('Labels'),
          <>
            {switchRow(
              'Show label color',
              settings.shouldShowContentLabelColor,
              (shouldShowContentLabelColor) =>
                onChange({ shouldShowContentLabelColor })
            )}
            {selectRow(
              'Color by',
              [
                { label: PRIMARY_LABEL, value: '' },
                ...labelSets.map((set) => ({
                  label: set.label,
                  value: set.id,
                })),
              ],
              settings.colorLabelSetId ?? '',
              (next) =>
                onChange({ colorLabelSetId: next === '' ? undefined : next })
            )}
          </>,
          styles.LABEL_ROW_GAP
        )}

        <hr css={styles.viewMenuDivider} />

        {section(
          <div css={styles.viewMenuTitleGroup}>
            {title('Other Details')}
            <p css={styles.viewMenuNote}>
              Some attributes only display in card density.
            </p>
          </div>,
          detailRows(CONTENT_DETAILS),
          styles.DETAIL_ROW_GAP
        )}
      </>
    )
  }

  return (
    <>
      {section(
        title('Include'),
        KINDS.map(objectRow),
        styles.OBJECT_ROW_GAP,
        styles.INCLUDE_HEADING_GAP
      )}

      <hr css={styles.viewMenuDivider} />

      <div css={styles.viewMenuBlock}>
        {segmentRow(
          'Grid',
          WEEKS_MODES,
          settings.weeksMode,
          (weeksMode) => onChange({ weeksMode }),
          { isTitle: true }
        )}
      </div>
    </>
  )
}
ViewMenu.displayName = 'Calendar: ViewMenu'
